<template>
  <div>
    <!-- Menu Items -->
    <div>
      <div class="menu-item" v-for="(menu_item, m_index) in menu_items" :key="m_index"
           :class="isUrl(menu_item.url) ? ' active' : ''" v-on="menu_item.submenu?{click: (e) => addActiveClass(e)}:{}">
        <div v-if="menu_item.submenu" class="flex items-center py-3 group menu-link have-sub-menu" style="cursor:pointer;">
          <icon :name="menu_item.icon" class="w-5 h-5 mr-3 rtl:ml-3 menu__icon" />
          <div class="menu__name">{{ __(menu_item.name) }}</div>
        </div>
        <Link v-else class="flex items-center py-3 group menu-link" :href="route(menu_item.route)">
          <icon :name="menu_item.icon" class="w-5 h-5 mr-3 rtl:ml-3 menu__icon" />
          <div class="menu__name">{{ __(menu_item.name) }}</div>
        </Link>
        <div v-if="menu_item.submenu" class="sub-menu-items" @click.stop>
          <Link class="sub-menu-item" v-for="(sub_menu_item, s_m_index) in menu_item.submenu" :key="s_m_index"
              :class="this.isUrl(sub_menu_item.url) ? ' active' : ''" :href="sub_menu_item.path ?? (sub_menu_item.param?route(sub_menu_item.route,sub_menu_item.param):route(sub_menu_item.route))">
              <icon v-if="sub_menu_item.icon" :name="sub_menu_item.icon" class="w-4 h-4 mr-1 rtl:ml-1 menu__icon" />
              <icon v-else name="dash" class="w-4 h-4 mr-1 rtl:mr-1 menu__icon" />
              <div class="menu__name">{{ __(sub_menu_item.name) }}</div>
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import { Link } from '@inertiajs/vue3'

export default {
  components: { Icon, Link },

  data() {
    return {
      user: null,
      menu_items: [],
      enable_option: {}
    }
  },

  computed: {
    roleSlug() {
      return this.user?.role?.slug ?? ''
    },
    isSystemAdmin() {
      return this.roleSlug === 'system_admin'
    },
    isLocalAdmin() {
      return this.roleSlug === 'local_admin'
    },
    isComplaintManager() {
      return this.roleSlug === 'complaint_manager'
    },
    isSupervisor() {
      return this.roleSlug === 'supervisor'
    },
    isRestricted() {
      return this.roleSlug === 'restricted_profile'
    },
  },

  methods: {
    isUrl(...urls) {
      let currentUrl = this.$page.url.substr(1)
      currentUrl = currentUrl.replace('dashboard/', '')
      if (urls[0] === '') return currentUrl === ''
      return urls.filter(url => currentUrl.startsWith(url)).length
    },
    addActiveClass(e) {
      e.currentTarget.classList.toggle('hover')
    },

    // ─── Construction du menu selon le rôle ───────────────────────────────────
    buildMenu() {
      const access = this.user.access
      const role   = this.roleSlug
      const items  = []

      // ── Dashboard (tous les rôles) ──
      items.push({ name: 'Dashboard', route: 'dashboard', url: 'dashboard', icon: 'dashboard' })

      // ── Tickets ──
      // Restricted : lecture seule → on affiche quand même (accès read=true dans le seeder)
      if (access.ticket?.read || access.ticket?.create || access.ticket?.update) {
        items.push({ name: 'Tickets', route: 'tickets', url: 'tickets', icon: 'ticket' })
      }

      // ── Contacts + Historiques (pas pour restricted_profile) ──
      if (!this.isRestricted && Object.values(access.contact || {}).some(Boolean)) {
        items.push({ name: 'Contacts',            route: 'contacts', url: 'contacts', icon: 'contact' })
        items.push({ name: 'Historique des appels', route: 'calls',    url: 'calls',    icon: 'phone'   })
        items.push({ name: 'Historique des sms',   route: 'sms',      url: 'sms',      icon: 'message' })
      }

      // ── Gestion des utilisateurs ──
      // Rôles utilisateurs : system_admin uniquement
      // Liste users : system_admin + local_admin
      const userSubmenus = []
      if (this.isSystemAdmin) {
        userSubmenus.push({ name: 'Rôles utilisateurs', route: 'roles', url: 'users/roles', icon: 'user_role' })
      }
      if (Object.values(access.user || {}).some(Boolean)) {
        userSubmenus.push({ name: 'Utilisateurs', route: 'users', url: 'users/all', icon: 'users' })
      }
      if (userSubmenus.length) {
        items.push({ name: 'Utilisateurs', route: '', url: 'users', icon: 'users', submenu: userSubmenus })
      }

      // ── Paramètres (system_admin + local_admin uniquement) ──
      if (this.isSystemAdmin || this.isLocalAdmin) {
        const settingSubmenus = []

        // Global : system_admin seulement
        if (this.isSystemAdmin) {
          settingSubmenus.push({ name: 'Global', route: 'global', url: 'settings/global', icon: 'global_setting' })
        }

        if (Object.values(access.department || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'ServicesLabel',    route: 'departments', url: 'settings/departments', icon: 'departments' })
        }
        if (Object.values(access.category || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'Catégories',  route: 'categories',  url: 'settings/categories',  icon: 'category'    })
        }
        if (Object.values(access.type || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'Types',       route: 'types',       url: 'settings/types',       icon: 'types'       })
        }
        if (Object.values(access.status || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'Statuts',     route: 'statuses',    url: 'settings/statuses',    icon: 'status'      })
        }
        if (Object.values(access.priority || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'Priorités',   route: 'priorities',  url: 'settings/priorities',  icon: 'priorities'  })
        }

        // Email templates & SMTP : system_admin seulement
        if (this.isSystemAdmin) {
          if (access.email_template?.read) {
            settingSubmenus.push({ name: 'Modèles d\'email', route: 'templates',     url: 'settings/templates', icon: 'email'          })
          }
          if (access.smtp?.read) {
            settingSubmenus.push({ name: 'Paramètres SMTP',  route: 'settings.smtp', url: 'settings/smtp',      icon: 'email_template' })
            settingSubmenus.push({ name: 'Journal d\'activités', route: 'activity_logs', url: 'activity-logs',  icon: 'global_setting' })
          }
        }

        // Listes blanche/noire IVR — URLs directes (évite Ziggy pour ces routes)
        if (Object.values(access.phone_list || {}).some(Boolean)) {
          settingSubmenus.push({ name: 'Liste blanche', path: '/dashboard/settings/phone-lists/whitelist', url: 'settings/phone-lists/whitelist', icon: 'check'          })
          settingSubmenus.push({ name: 'Liste noire',   path: '/dashboard/settings/phone-lists/blacklist', url: 'settings/phone-lists/blacklist', icon: 'status'         })
        }
        if (access.phone_list_log?.read) {
          settingSubmenus.push({ name: 'Journal IVR',   path: '/dashboard/settings/phone-lists/logs',      url: 'settings/phone-lists/logs',      icon: 'global_setting' })
        }

        // Configuration API (anciennement Config Compte)
        settingSubmenus.push({ name: 'Configuration API', route: 'account.config', url: 'dashboard/account/config', icon: 'gear' })

        if (settingSubmenus.length) {
          items.push({ name: 'Paramètres', route: '', url: 'settings', icon: 'settings', submenu: settingSubmenus })
        }
      }

      this.menu_items = items
    }
  },

  created() {
    this.user = this.$page.props.auth.user

    if (this.$page.props.enable_options) {
      let options = JSON.parse(this.$page.props.enable_options.value)
      options.forEach(option => {
        this.enable_option[option.slug] = !!option.value
      })
    }

    this.buildMenu()
  }
}
</script>
